/**
 * LocationService — Centralized User Position
 *
 * Single source of truth for:
 * - Raw GPS position (from browser Geolocation API)
 * - Snapped position (from Artrack Snap-to-Path API)
 * - Active route + along-route distance
 * - Distance to any POI (airline + route-based)
 *
 * Replaces scattered GPS logic in NavigatePage, useKnowledgeGuide, etc.
 */

import { haversine, isInSchlucht, formatWalkingTime, formatDistance } from './geo'
import { snapToNearestRoute, buildRoutePolyline, type RoutePolyline, type GpsPoint } from './snapToPath'
import { ARTRACK_API_BASE, API_KEY } from '../guide/constants'
// tile-map (+canvas-engine +hls.js ≈ 2.7 MB) must not enter the initial
// bundle: this module is reachable from the app root via the global
// voice-agent. The service is only needed once loadRoutes() runs, so it
// is imported dynamically there (route-splitting 2026-07-03).

// ── Types ───────────────────────────────────────────────────────

export interface LatLng {
  lat: number
  lng: number
}

export interface SnapResult {
  snappedLat: number
  snappedLon: number
  distanceMeters: number
  alongMeters: number
  lateralOffsetMeters: number
  routeId: number
}

export interface UserPosition {
  /** Raw GPS coordinates */
  raw: LatLng
  /** Snapped to route (null if not near a route) */
  snapped: LatLng | null
  /** Snap metadata */
  snap: SnapResult | null
  /** Whether user is within the Tscheppaschlucht bounding box */
  inSchlucht: boolean
  /** GPS accuracy in meters (if available) */
  accuracy: number | null
  /** Timestamp of position update */
  timestamp: number
}

export interface POIDistance {
  /** Straight-line distance in meters */
  airline: number
  /** Distance along route in meters (null if not on same route) */
  alongRoute: number | null
  /** Formatted walking time along route ("5 Min.") or null */
  walkingTime: string | null
  /** Formatted distance string ("234 m" or "1.2 km") */
  formatted: string
  /** Whether this is a route-based distance (more accurate) or airline */
  isRouteDistance: boolean
  /** True when the distance is measured FROM THE ROUTE START (km 0) —
   *  shown to remote visitors / without GPS instead of their own
   *  meaningless airline distance (Alex 2026-07-04). */
  fromStart: boolean
}

export type PositionListener = (position: UserPosition) => void

// ── Configuration ───────────────────────────────────────────────

export interface LocationServiceConfig {
  /** Track ID for snap-to-path (default: 30) */
  trackId: number
  /** Minimum ms between snap API calls (default: 3000) */
  snapThrottleMs: number
  /** Maximum distance from path to accept snap in meters (default: 150) */
  maxSnapDistance: number
  /** Override GPS with fixed position (for debugging) */
  debugPosition: LatLng | null
  /** Walking speed in km/h for time estimates (default: 3.5) */
  walkingSpeedKmh: number
  /** Reject a GPS sample whose implied speed since the last accepted fix
   *  exceeds this (m/s). 8 m/s ≈ 29 km/h — far above walking/running, so a
   *  legitimate move never trips it, but a GPS spike does. Spikes were the
   *  cause of the wrong-narration jumps ("Flying Fox" far away): the raw
   *  spike got snapped to the nearest route point and triggered that POI. */
  maxPlausibleSpeedMps: number
  /** After this many consecutive rejects, accept the next sample anyway so a
   *  genuine teleport (tunnel exit, cold fix) can never freeze the dot. */
  gpsRejectStreakMax: number
}

import { getConfig } from '../config/project'

const DEFAULT_CONFIG: LocationServiceConfig = {
  trackId: getConfig().api.trackId,
  snapThrottleMs: 3000,
  maxSnapDistance: 150,
  debugPosition: null,
  walkingSpeedKmh: 3.5,
  maxPlausibleSpeedMps: 8,
  gpsRejectStreakMax: 3,
}

// ── LocationService ─────────────────────────────────────────────

export class LocationService {
  private config: LocationServiceConfig
  private listeners: Set<PositionListener> = new Set()
  private watchId: number | null = null
  private currentPosition: UserPosition | null = null

  // Snap state
  private lastSnapTime = 0
  private lastSnapResult: SnapResult | null = null
  private pendingSnap: Promise<SnapResult | null> | null = null

  // POI snap data cache (waypointId → { routeId → alongMeters })
  private poiSnapCache = new Map<number, Map<number, number>>()

  // Client-side route polylines for local snapping
  private routePolylines: RoutePolyline[] = []
  private routesLoaded = false

  // GPS spike gate (B1): last raw fix we accepted + a bounded reject streak.
  private lastAcceptedLatLng: LatLng | null = null
  private lastAcceptedAt = 0
  private gpsRejectStreak = 0

  // Persisted active route (C1): survives an app restart so the WebApp can
  // resume onto the route the user was actually on instead of jumping to the
  // nearest POI (Alex: "merkt nicht, welche Route ich auf Start gedrückt habe").
  private static readonly ROUTE_KEY = 'tschepp-active-route'
  /** Der gemerkte Eintrag mitsamt `explicit`-Flag — für Aufrufer, die
   *  unterscheiden müssen, ob der Nutzer die Route bewusst gewählt hat
   *  oder ob der GPS-Auto-Latch sie nur beobachtet hat. Siehe
   *  `chosenRouteId`. */
  static getPersistedRouteEntry(): { routeId: number; explicit: boolean } | null {
    try {
      const raw = localStorage.getItem(LocationService.ROUTE_KEY)
      if (!raw) return null
      const { routeId, ts, explicit } = JSON.parse(raw)
      if (typeof routeId !== 'number' || typeof ts !== 'number') return null
      if (Date.now() - ts > 12 * 60 * 60 * 1000) return null
      // Alt-Einträge ohne Flag stammen aus der Zeit vor der Trennung und
      // gelten als bewusst — dieselbe Lesart wie in setPersistedRoute.
      return { routeId, explicit: explicit !== false }
    } catch {
      return null
    }
  }

  /** Last active route id, or null if none/stale (>12h). Read at app init. */
  static getPersistedRoute(): number | null {
    try {
      const raw = localStorage.getItem(LocationService.ROUTE_KEY)
      if (!raw) return null
      const { routeId, ts } = JSON.parse(raw)
      if (typeof routeId !== 'number' || typeof ts !== 'number') return null
      if (Date.now() - ts > 12 * 60 * 60 * 1000) return null
      return routeId
    } catch {
      return null
    }
  }
  /** Persist the active route.
   *
   *  `explicit` (Default true) = der Nutzer hat sie bewusst aktiviert
   *  (Auswählen / Route starten). Der GPS-Auto-Latch unten ruft mit
   *  `explicit: false` und darf eine bewusste Wahl NICHT überschreiben:
   *  die Routen der Schlucht überlappen, `snapToNearestRoute` nimmt die
   *  jeweils nächste — ohne diese Sperre hätte der nächste GPS-Tick eine
   *  gerade getroffene Wahl innerhalb einer Sekunde wieder überschrieben
   *  (aufgefallen 2026-07-25, als Browsen und Aktivieren getrennt wurden).
   *  Für die LIVE-Anzeige ist das ohne Belang — `confidentRouteId` bevorzugt
   *  ohnehin den echten Snap; hier geht es nur darum, was einen Neustart
   *  überlebt. */
  /** Gewaehlte Route verwerfen — noetig fuer den Onboarding-Seed (#807):
   *  Eine per Query gesetzte, aber laut Routenliste unbekannte Route muss
   *  fail-closed auf „keine Route" fallen, nie still auf eine andere. */
  static clearPersistedRoute(): void {
    try { localStorage.removeItem(LocationService.ROUTE_KEY) } catch { /* non-fatal */ }
  }

  static setPersistedRoute(routeId: number, opts?: { explicit?: boolean }): void {
    try {
      const explicit = opts?.explicit !== false
      if (!explicit) {
        const prev = localStorage.getItem(LocationService.ROUTE_KEY)
        if (prev) {
          const p = JSON.parse(prev)
          const fresh = typeof p?.ts === 'number' && Date.now() - p.ts <= 12 * 60 * 60 * 1000
          // Alt-Einträge ohne Flag stammen aus der Zeit vor der Trennung —
          // als bewusst behandeln, statt sie automatisch zu überschreiben.
          if (fresh && p?.explicit !== false) return
        }
      }
      localStorage.setItem(LocationService.ROUTE_KEY, JSON.stringify({ routeId, ts: Date.now(), explicit }))
    } catch { /* private mode / quota — non-fatal */ }
  }

  constructor(config: Partial<LocationServiceConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
  }

  // ── Public API ──────────────────────────────────────────────

  /** Get current position (or null if not tracking) */
  get position(): UserPosition | null {
    return this.currentPosition
  }

  /** Get current route ID (from snap, or null) */
  get activeRouteId(): number | null {
    return this.currentPosition?.snap?.routeId ?? null
  }

  /** B2 — the route the user is *confidently* on: a tight live snap wins,
   *  otherwise the persisted route from the last session. This is what the
   *  UI should pre-select at start instead of auto-jumping to the nearest
   *  POI (Alex: "bin am Wasserweg, kann aber nicht umstellen"). Returns null
   *  only when we have neither a close snap nor a remembered route. */
  get confidentRouteId(): number | null {
    const snap = this.currentPosition?.snap
    if (snap && snap.distanceMeters <= this.config.maxSnapDistance / 2) {
      return snap.routeId
    }
    return LocationService.getPersistedRoute()
  }

  /** Die Route, an der ERZÄHLUNG und FORTSCHRITT hängen (Issue #401).
   *
   *  Unterschied zu `confidentRouteId`, und der ist wesentlich: dort gewinnt
   *  der Live-Snap. Für die Vorauswahl eines Pins ist das richtig — für die
   *  Erzählung wäre es fatal. Die vier Routen der Schlucht starten am
   *  IDENTISCHEN Punkt (gemessen: 0 m Abstand) und überlappen ein Stück weit;
   *  `snapToNearestRoute` nimmt dort die jeweils nächste, also mal die eine,
   *  mal die andere. Hinge die Erzählung daran, spränge sie am Einstieg
   *  zwischen zwei Routen hin und her und fiele jedes Mal zurück auf idle.
   *
   *  Deshalb ausschließlich die BEWUSSTE Wahl des Nutzers („Auswählen" /
   *  „Route starten"). Liegt keine vor, gibt der Getter `null` zurück und der
   *  Aufrufer bleibt bei seinem Standard — die Erzählung folgt dann NICHT dem
   *  GPS.
   *
   *  Das ist bewusst konservativ und eine Zwischenstufe, kein Endzustand.
   *  Naheliegend wäre, ohne bewusste Wahl auf `confidentRouteId` zurück-
   *  zufallen; genau das führt aber die Flackerquelle wieder ein, denn der
   *  Auto-Latch schreibt bei jedem Snap. Damit die Erzählung dem GPS folgen
   *  KANN, bräuchte es eine Hysterese: erst hinter der Weggabelung umschalten,
   *  wo die Routen eindeutig auseinanderlaufen. Solange die fehlt, ist
   *  „bleibt beim Standard" das ehrlichere Verhalten — es ist exakt das von
   *  heute und damit kein Rückschritt, während eine bewusste Wahl endlich
   *  wirkt.
   */
  get chosenRouteId(): number | null {
    const entry = LocationService.getPersistedRouteEntry()
    return entry?.explicit ? entry.routeId : null
  }

  /** Get distance along current route in meters (or null) */
  get alongMeters(): number | null {
    return this.currentPosition?.snap?.alongMeters ?? null
  }

  /** Whether currently tracking GPS */
  get isTracking(): boolean {
    return this.watchId !== null || this.config.debugPosition !== null
  }

  /** Subscribe to position updates */
  onPositionChanged(listener: PositionListener): () => void {
    this.listeners.add(listener)
    // Send current position immediately if available
    if (this.currentPosition) {
      listener(this.currentPosition)
    }
    return () => this.listeners.delete(listener)
  }

  /** Start GPS tracking */
  startTracking(): void {
    // Debug mode: emit fixed position
    if (this.config.debugPosition) {
      console.log('[LocationService] Debug mode — using fixed position')
      this.handleRawPosition(
        this.config.debugPosition.lat,
        this.config.debugPosition.lng,
        0,
      )
      return
    }

    if (this.watchId !== null) return
    if (!navigator.geolocation) {
      console.warn('[LocationService] Geolocation not available')
      return
    }

    // Get initial position
    navigator.geolocation.getCurrentPosition(
      (pos) => this.handleRawPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
      (err) => console.warn('[LocationService] Initial GPS error:', err.message),
      { enableHighAccuracy: true, timeout: 8000 },
    )

    // Continuous tracking
    this.watchId = navigator.geolocation.watchPosition(
      (pos) => this.handleRawPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000 },
    )

    console.log('[LocationService] GPS tracking started')
  }

  /** Stop GPS tracking */
  stopTracking(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId)
      this.watchId = null
      console.log('[LocationService] GPS tracking stopped')
    }
  }

  /** Manually set position (e.g., from external GPS source or debug) */
  setPosition(lat: number, lng: number, accuracy: number | null = null): void {
    this.handleRawPosition(lat, lng, accuracy)
  }

  /**
   * Calculate distance from current position to a POI.
   * Uses route-based distance if both are on the same route.
   */
  /** Signed along-route delta (POI − user) in metres on the user's CURRENT
   *  route: positive = ahead (toward the Ziel), negative = behind. The gorge
   *  trail is linear, so "ahead" is well-defined (Alex 2026-07-16). Returns
   *  null without GPS/snap or when the POI can't be snapped to the route. */
  signedAlongTo(poiLat: number, poiLng: number): number | null {
    const pos = this.currentPosition
    if (!pos?.snap || this.routePolylines.length === 0) return null
    const routeLine = this.routePolylines.find((r) => r.routeId === pos.snap!.routeId)
    const snap = snapToNearestRoute(poiLat, poiLng, routeLine ? [routeLine] : this.routePolylines)
    if (!snap) return null
    return snap.alongMeters - pos.snap.alongMeters
  }

  distanceToPOI(
    poiLat: number,
    poiLng: number,
    poiWaypointId?: number,
  ): POIDistance {
    const pos = this.currentPosition

    // Remote or no GPS: the visitor's own distance is meaningless (hundreds
    // of km) — measure ALONG THE ROUTE FROM THE START instead (km 0 → POI),
    // Alex 2026-07-04. On-site = GPS within 500 m of a route (snap distance).
    const ONSITE_MAX_METERS = 500
    const routeDist = pos ? this.distanceToRoute(pos.raw.lat, pos.raw.lng) : null
    const onSite = pos != null && (routeDist != null ? routeDist <= ONSITE_MAX_METERS : pos.inSchlucht)
    if (!onSite && this.routePolylines.length > 0) {
      const snap = snapToNearestRoute(poiLat, poiLng, this.routePolylines)
      if (snap) {
        const along = snap.alongMeters
        return {
          airline: pos ? haversine(pos.raw.lat, pos.raw.lng, poiLat, poiLng) : Infinity,
          alongRoute: along,
          walkingTime: formatWalkingTime(along, this.config.walkingSpeedKmh),
          formatted: formatDistance(along),
          isRouteDistance: true,
          fromStart: true,
        }
      }
    }

    if (!pos) {
      return { airline: Infinity, alongRoute: null, walkingTime: null, formatted: '—', isRouteDistance: false, fromStart: false }
    }

    const airline = haversine(pos.raw.lat, pos.raw.lng, poiLat, poiLng)

    // Try route-based distance using pre-computed snap data
    if (pos.snap && poiWaypointId) {
      // First check: same route
      const poiSnap = this.getPoiSnapAlongMeters(poiWaypointId, pos.snap.routeId)
      if (poiSnap !== null) {
        const alongRoute = Math.abs(poiSnap - pos.snap.alongMeters)
        const walkingTime = formatWalkingTime(alongRoute, this.config.walkingSpeedKmh)
        return {
          airline,
          alongRoute,
          walkingTime,
          formatted: formatDistance(alongRoute),
          isRouteDistance: true,
          fromStart: false,
        }
      }

      // Second check: POI is on any route — find best match
      const allRoutes = this.poiSnapCache.get(poiWaypointId)
      if (allRoutes && allRoutes.size > 0) {
        // Use the route with the shortest distance to user
        let bestDistance = Infinity
        let bestWalkingTime: string | null = null
        for (const [routeId, poiAlongMeters] of allRoutes) {
          // If user is on this route, calculate along-route distance
          if (routeId === pos.snap.routeId) {
            const d = Math.abs(poiAlongMeters - pos.snap.alongMeters)
            if (d < bestDistance) {
              bestDistance = d
              bestWalkingTime = formatWalkingTime(d, this.config.walkingSpeedKmh)
            }
          }
        }
        if (bestDistance < Infinity) {
          return {
            airline,
            alongRoute: bestDistance,
            walkingTime: bestWalkingTime,
            formatted: formatDistance(bestDistance),
            isRouteDistance: true,
            fromStart: false,
          }
        }
      }
    }

    // Fallback: use airline but still format as walking distance
    // In the Schlucht, there's always a route — airline is a reasonable approximation
    // for POIs without snap data, multiply by ~1.3 for path winding factor
    const estimatedRoute = airline * 1.3
    return {
      airline,
      alongRoute: null,
      walkingTime: formatWalkingTime(estimatedRoute, this.config.walkingSpeedKmh),
      formatted: formatDistance(estimatedRoute),
      isRouteDistance: false,
      fromStart: false,
    }
  }

  /**
   * Register a POI's snap data (pre-computed along-route position).
   * Called when loading POI/Waypoint data that includes snap metadata.
   */
  registerPOISnap(waypointId: number, routeId: number, alongMeters: number): void {
    if (!this.poiSnapCache.has(waypointId)) {
      this.poiSnapCache.set(waypointId, new Map())
    }
    this.poiSnapCache.get(waypointId)!.set(routeId, alongMeters)
  }

  /** Get cached along-meters for a POI on a specific route */
  getPoiSnapAlongMeters(waypointId: number, routeId: number): number | null {
    return this.poiSnapCache.get(waypointId)?.get(routeId) ?? null
  }

  /** Reset all state */
  reset(): void {
    this.stopTracking()
    this.currentPosition = null
    this.lastSnapTime = 0
    this.lastSnapResult = null
    this.pendingSnap = null
    this.poiSnapCache.clear()
    this.listeners.clear()
  }

  /** Update config at runtime */
  configure(config: Partial<LocationServiceConfig>): void {
    Object.assign(this.config, config)
  }

  /**
   * Load route GPS points for client-side snapping.
   * Call once at startup — after this, snap works without API calls.
   */
  async loadRoutes(routeIds: number[]): Promise<void> {
    if (this.routesLoaded) return

    console.log(`[LocationService] Loading ${routeIds.length} routes for client-side snap...`)
    const polylines: RoutePolyline[] = []

    // Route the fetches through tile-map's artrackService: it single-flight
    // caches gps-points per route, so this shares the download the map view
    // already triggered instead of re-fetching every route (startup dedup).
    const { artrackService } = await import('@apopovic77/tile-map')
    const results = await Promise.all(routeIds.map(async (routeId) => {
      try {
        const points: GpsPoint[] = await artrackService.getRouteGpsPoints(routeId)
        return { routeId, points }
      } catch (e) {
        console.warn(`[LocationService] Failed to load route ${routeId}:`, e)
        return { routeId, points: [] as GpsPoint[] }
      }
    }))
    for (const { routeId, points } of results) {
      if (points.length < 2) continue
      const poly = buildRoutePolyline(routeId, points)
      polylines.push(poly)
      console.log(`[LocationService] Route ${routeId}: ${points.length} points, ${poly.totalMeters.toFixed(0)}m`)
    }

    this.routePolylines = polylines
    this.routesLoaded = true
    console.log(`[LocationService] ${polylines.length} routes loaded for client-side snap`)
  }

  /** Check if routes are loaded for client-side snap */
  get hasRoutes(): boolean {
    return this.routesLoaded && this.routePolylines.length > 0
  }

  /**
   * Raw distance (meters) from a position to the nearest loaded route —
   * the snap-to-path distance WITHOUT the maxSnapDistance cut-off, so
   * callers can classify "on site vs far away" (e.g. the default camera
   * choice on the map). Returns null while routes aren't loaded yet.
   */
  distanceToRoute(lat: number, lng: number): number | null {
    if (this.routePolylines.length === 0) return null
    return snapToNearestRoute(lat, lng, this.routePolylines)?.distanceMeters ?? null
  }

  /**
   * Snap an arbitrary position onto the nearest loaded route and return the
   * corrected coordinate (no maxSnapDistance cut-off — caller decides).
   * Used to draw Flora & Fauna sighting pins ON the trail instead of at
   * their raw GPS scatter. Returns null while routes aren't loaded.
   */
  snapPosition(lat: number, lng: number): { lat: number; lng: number; distanceMeters: number } | null {
    if (this.routePolylines.length === 0) return null
    const snap = snapToNearestRoute(lat, lng, this.routePolylines)
    if (!snap) return null
    return { lat: snap.snappedLat, lng: snap.snappedLon, distanceMeters: snap.distanceMeters }
  }

  /**
   * Auf EINE bestimmte Route schnappen — nicht auf die naechstgelegene.
   *
   * Anlass (Alex im Feld, 2026-08-10, Aufnahme 118779): Der Positionspfeil
   * stand neben der gezeichneten Route im Wald. Er war nicht ungeschnappt —
   * er war auf eine ANDERE der vier Routen geschnappt, die dort naeher lag
   * und gar nicht gezeichnet wird. „Naechste Route" und „die Route, die der
   * Besucher sieht" sind zwei verschiedene Dinge; fuer die Anzeige zaehlt die
   * zweite.
   *
   * Ohne Kappung — der Aufrufer entscheidet, ab welcher Abweichung er lieber
   * die rohe Position zeigt.
   */
  snapToRoute(lat: number, lng: number, routeId: number): SnapResult | null {
    const line = this.routePolylines.find((r) => r.routeId === routeId)
    if (!line) return null
    return snapToNearestRoute(lat, lng, [line])
  }

  // ── Private ─────────────────────────────────────────────────

  private async handleRawPosition(lat: number, lng: number, accuracy: number | null): Promise<void> {
    // B1 — spike gate. Drop a sample whose implied speed since the last
    // accepted fix is physically impossible for a walker; a bounded streak
    // guarantees we never get stuck rejecting a genuine teleport.
    const now = Date.now()
    if (this.lastAcceptedLatLng) {
      const dtSec = (now - this.lastAcceptedAt) / 1000
      if (dtSec > 0) {
        const dist = haversine(this.lastAcceptedLatLng.lat, this.lastAcceptedLatLng.lng, lat, lng)
        const speed = dist / dtSec
        if (speed > this.config.maxPlausibleSpeedMps && this.gpsRejectStreak < this.config.gpsRejectStreakMax) {
          this.gpsRejectStreak++
          return // ignore the spike; wait for the next fix
        }
      }
    }
    this.gpsRejectStreak = 0
    this.lastAcceptedLatLng = { lat, lng }
    this.lastAcceptedAt = now

    const inSchlucht = isInSchlucht(lat, lng)

    // Build position (initially without snap)
    const position: UserPosition = {
      raw: { lat, lng },
      snapped: null,
      snap: null,
      inSchlucht,
      accuracy: accuracy ?? null,
      timestamp: Date.now(),
    }

    // Client-side snap (instant, no API call) — preferred
    if (this.routePolylines.length > 0) {
      const snap = snapToNearestRoute(lat, lng, this.routePolylines)
      if (snap && snap.distanceMeters <= this.config.maxSnapDistance) {
        position.snapped = { lat: snap.snappedLat, lng: snap.snappedLon }
        position.snap = snap
      }
    } else {
      // Fallback: API-based snap (throttled)
      const snap = await this.snapToPathAPI(lat, lng)
      if (snap && snap.distanceMeters <= this.config.maxSnapDistance) {
        position.snapped = { lat: snap.snappedLat, lng: snap.snappedLon }
        position.snap = snap
      }
    }

    // C1 — persist the route we are confidently on (close snap) so a restart
    // can resume onto it. Only a tight snap counts, to avoid latching a route
    // we merely passed near.
    if (position.snap && position.snap.distanceMeters <= this.config.maxSnapDistance / 2) {
      LocationService.setPersistedRoute(position.snap.routeId, { explicit: false })
    }

    this.currentPosition = position
    this.notifyListeners(position)
  }

  private notifyListeners(position: UserPosition): void {
    for (const listener of this.listeners) {
      try {
        listener(position)
      } catch (e) {
        console.error('[LocationService] Listener error:', e)
      }
    }
  }

  private async snapToPathAPI(lat: number, lng: number): Promise<SnapResult | null> {
    const now = Date.now()

    // Throttle
    if (now - this.lastSnapTime < this.config.snapThrottleMs && this.lastSnapResult) {
      return this.lastSnapResult
    }

    // Deduplicate concurrent requests
    if (this.pendingSnap) {
      return await this.pendingSnap
    }

    this.pendingSnap = this.fetchSnap(lat, lng)
    try {
      const result = await this.pendingSnap
      this.lastSnapTime = now
      this.lastSnapResult = result
      return result
    } catch {
      return this.lastSnapResult // fallback to last known
    } finally {
      this.pendingSnap = null
    }
  }

  private async fetchSnap(lat: number, lng: number): Promise<SnapResult | null> {
    try {
      const res = await fetch(`${ARTRACK_API_BASE}/snap/${this.config.trackId}/snap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-KEY': API_KEY },
        body: JSON.stringify({ lat, lon: lng }),
      })
      if (!res.ok) return null
      return await res.json() as SnapResult
    } catch {
      return null
    }
  }
}

// ── Singleton ───────────────────────────────────────────────────

let _instance: LocationService | null = null

/** Get the shared LocationService instance */
export function getLocationService(config?: Partial<LocationServiceConfig>): LocationService {
  if (!_instance) {
    _instance = new LocationService(config)
  } else if (config) {
    _instance.configure(config)
  }
  return _instance
}

/** Reset the singleton (for testing) */
export function resetLocationService(): void {
  _instance?.reset()
  _instance = null
}
